/**
 * WalkCompanion Azure AD Authentication Service
 * Handles enterprise authentication with Azure AD
 */

import { AuthService } from './AuthService';
import { Organization, User, AuthProvider } from '../types';
import { getAzureADConfig, getRedirectUri, AzureADPlatformConfig } from '../config/azureADConfig';

export interface AzureADConfig {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  authority: string;
}

export interface AzureADTokenResponse {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  refresh_token?: string;
}

export interface AzureADUserInfo {
  sub: string;
  name: string;
  family_name: string;
  given_name: string;
  email: string;
  oid: string;
  tid: string;
  upn: string;
}

export interface AzureADAuthResult {
  user: User;
  organization: Organization;
  tokens: AzureADTokenResponse;
}

export class AzureADAuthService {
  private authService: AuthService;
  private config: AzureADPlatformConfig;
  private currentOrganization: Organization | null = null;

  constructor(authService: AuthService, platform: 'web' | 'mobile', os?: 'ios' | 'android') {
    this.authService = authService;
    this.config = getAzureADConfig(platform);
    
    // Override redirect URI for mobile with OS-specific URI
    if (platform === 'mobile' && os) {
      this.config = {
        ...this.config,
        redirectUri: getRedirectUri(platform, os)
      };
    }
  }

  /**
   * Build Azure AD authorization URL
   */
  public buildAuthUrl(_organization: Organization, state?: string): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      redirect_uri: this.config.redirectUri,
      scope: this.config.scopes.join(' '),
      response_mode: 'query',
      prompt: 'select_account',
      state: state || this.generateState(),
    });

    return `${this.config.authority}/oauth2/v2.0/authorize?${params.toString()}`;
  }

  /**
   * Exchange authorization code for tokens
   */
  public async exchangeCodeForTokens(code: string): Promise<AzureADTokenResponse> {
    try {
      const response = await fetch(`${this.config.authority}/oauth2/v2.0/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.config.clientId,
          scope: this.config.scopes.join(' '),
          code: code,
          redirect_uri: this.config.redirectUri,
          grant_type: 'authorization_code',
        }).toString(),
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.statusText}`);
      }

      const data = await response.json() as AzureADTokenResponse;
      return data;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      throw new Error('Failed to exchange authorization code for tokens');
    }
  }

  /**
   * Get user information from Azure AD
   */
  public async getUserInfo(accessToken: string): Promise<AzureADUserInfo> {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get user info: ${response.statusText}`);
      }

      const data = await response.json() as AzureADUserInfo;
      return data;
    } catch (error) {
      console.error('Error getting user info:', error);
      throw new Error('Failed to get user information from Azure AD');
    }
  }

  /**
   * Sign in with Azure AD
   */
  public async signInWithAzureAD(
    organization: Organization,
    authCode: string
  ): Promise<AzureADAuthResult> {
    try {
      console.log('🔄 Starting Azure AD sign-in for organization:', organization.name);

      // Exchange code for tokens
      const tokens = await this.exchangeCodeForTokens(authCode);
      console.log('✅ Tokens received successfully');

      // Get user information
      const userInfo = await this.getUserInfo(tokens.access_token);
      console.log('✅ User info received:', userInfo.email);

      // Create or find user in our system
      const user = await this.createOrFindUser(userInfo, organization);

      // Set the organization
      this.currentOrganization = organization;
      this.authService.setSelectedOrganization(organization);

      const result: AzureADAuthResult = {
        user,
        organization,
        tokens,
      };

      console.log('✅ Azure AD sign-in completed successfully');
      return result;

    } catch (error) {
      console.error('❌ Azure AD sign-in error:', error);
      throw new Error(`Azure AD sign-in failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create or find user in our system
   */
  private async createOrFindUser(
    userInfo: AzureADUserInfo,
    organization: Organization
  ): Promise<User> {
    try {
      // Check if user already exists
      const existingUser = await this.findUserByEmail(userInfo.email);
      
      if (existingUser) {
        console.log('🔍 Found existing user:', existingUser.id);
        
        // Update user with latest Azure AD info
        const updatedUser: User = {
          ...existingUser,
          displayName: userInfo.name,
          organizationId: organization.id,
          organizationName: organization.name,
          authProvider: 'azure' as AuthProvider,
        };

        // Update user in database
        await this.updateUserInDatabase(updatedUser);
        
        return updatedUser;
      } else {
        console.log('🆕 Creating new user for Azure AD sign-in');
        
        // Create new user
        const newUser: User = {
          id: userInfo.sub, // Use Azure AD sub as user ID
          email: userInfo.email,
          displayName: userInfo.name,
          profileImageURL: undefined,
          organizationId: organization.id,
          teamId: undefined,
          role: 'member',
          joinedDate: new Date().toISOString(),
          totalPoints: 0,
          streak: 0,
          authProvider: 'azure' as AuthProvider,
          organizationName: organization.name,
          teamName: undefined,
          profile: {
            displayName: userInfo.name,
            email: userInfo.email,
            joinedDate: new Date().toISOString(),
            organizationId: organization.id,
            organizationName: organization.name,
            role: 'member',
            streak: 0,
            totalPoints: 0,
          },
        };

        // Save user to database
        await this.saveUserToDatabase(newUser);
        
        return newUser;
      }

    } catch (error) {
      console.error('Error creating/finding user:', error);
      throw new Error('Failed to create or find user');
    }
  }

  /**
   * Find user by email in our database
   */
  private async findUserByEmail(_email: string): Promise<User | null> {
    try {
      // This would typically query your database
      // For now, return null to simulate new user creation
      return null;
    } catch (error) {
      console.error('Error finding user by email:', error);
      return null;
    }
  }

  /**
   * Save user to database
   */
  private async saveUserToDatabase(user: User): Promise<void> {
    try {
      // This would typically save to your database
      console.log('💾 Saving user to database:', user.id);
    } catch (error) {
      console.error('Error saving user to database:', error);
      throw new Error('Failed to save user to database');
    }
  }

  /**
   * Update user in database
   */
  private async updateUserInDatabase(user: User): Promise<void> {
    try {
      // This would typically update your database
      console.log('💾 Updating user in database:', user.id);
    } catch (error) {
      console.error('Error updating user in database:', error);
      throw new Error('Failed to update user in database');
    }
  }

  /**
   * Generate random state parameter for OAuth
   */
  private generateState(): string {
    // Use a simpler random generation for React Native compatibility
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Get current organization
   */
  public getCurrentOrganization(): Organization | null {
    return this.currentOrganization;
  }

  /**
   * Set organization configuration
   */
  public setOrganizationConfig(organization: Organization): void {
    this.currentOrganization = organization;
  }

  /**
   * Validate Azure AD configuration
   */
  public validateConfig(): boolean {
    return !!(
      this.config.clientId &&
      this.config.tenantId &&
      this.config.redirectUri &&
      this.config.authority
    );
  }

  /**
   * Get Azure AD configuration
   */
  public getConfig(): AzureADConfig {
    return { ...this.config };
  }
} 